import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
 
/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  SafeAreaView,
  StatusBar,
  Image,
  FlatList,
  RefreshControl,
  TextInput,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
export default function InterviewPrepScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_interviewprep`;
    apiFetch('/api/data/interviews?sort=interview_date&order=desc&limit=10').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  // States
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('prepare');
  const [jobPosition, setJobPosition] = useState('');
  const [company, setCompany] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('technical');
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswer, setUserAnswer] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);
  const [prepHistory, setPrepHistory] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  // Question categories
  const categories = [
    { id: 'technical', name: 'Technical', icon: 'code-outline' },
    { id: 'behavioral', name: 'Behavioral', icon: 'people-outline' },
    { id: 'experience', name: 'Experience', icon: 'briefcase-outline' },
    { id: 'company', name: 'Company', icon: 'business-outline' },
    { id: 'situational', name: 'Situational', icon: 'git-branch-outline' },
  ];

  // Mock interview history data
  const mockPrepHistory = [
    {
      id: '1',
      position: 'Senior Mechanical Engineer',
      company: 'Rolls-Royce',
      date: '15 May 2025',
      category: 'technical',
      questionsCount: 5,
      averageScore: 85,
    },
    {
      id: '2',
      position: 'Project Manager',
      company: 'BAE Systems',
      date: '12 May 2025',
      category: 'behavioral',
      questionsCount: 8,
      averageScore: 78,
    },
    {
      id: '3',
      position: 'Software Developer',
      company: 'Dyson',
      date: '10 May 2025',
      category: 'technical',
      questionsCount: 6,
      averageScore: 92,
    },
  ];

  // Mock generated questions
  const mockTechnicalQuestions = [
    {
      id: '1',
      question:
        'Can you explain the difference between stress and strain in mechanical engineering?',
      idealAnswer:
        'Stress is the internal resistance of an object to an external force, measured as force per unit area. Strain is the measure of deformation that occurs in response to stress, calculated as the change in dimension divided by the original dimension. Stress is measured in units like pascals (Pa), while strain is dimensionless.',
      tips: [
        "Reference Hooke's law",
        'Mention elastic vs. plastic deformation',
        'Include units of measurement',
      ],
    },
    {
      id: '2',
      question: 'Describe the working principle of a gas turbine engine.',
      idealAnswer:
        'A gas turbine engine operates on the Brayton cycle and consists of three main components: compressor, combustion chamber, and turbine. Air is drawn in and compressed, fuel is added and combusted to increase temperature and energy content, and the high-energy gas expands through the turbine, generating power. Some of this power drives the compressor, while the rest is used for propulsion or electricity generation.',
      tips: [
        'Mention thermodynamic cycle',
        "Explain each component's function",
        'Discuss efficiency factors',
      ],
    },
    {
      id: '3',
      question:
        'What is finite element analysis (FEA) and how is it used in mechanical engineering?',
      idealAnswer:
        'Finite Element Analysis is a computational method that divides a complex structure into smaller, simpler parts called finite elements. It is used to solve complex engineering problems by creating mathematical models that predict how a product reacts to real-world forces, vibration, heat, fluid flow, and other physical effects. In mechanical engineering, FEA helps with structural analysis, thermal analysis, vibration analysis, and optimization of designs before physical prototyping.',
      tips: [
        'Explain the discretization process',
        'Mention types of analysis possible',
        'Discuss advantages over physical testing',
      ],
    },
    {
      id: '4',
      question:
        'Explain the concept of GD&T (Geometric Dimensioning and Tolerancing) and why it is important in manufacturing.',
      idealAnswer:
        'GD&T is a symbolic language used on engineering drawings to define the allowable deviation of feature geometry. Unlike traditional tolerancing which only controls size, GD&T controls form, orientation, location, and runout using a system of symbols and rules. It is important in manufacturing because it ensures parts will assemble properly and function as intended, improves communication between design and manufacturing, reduces ambiguity, and allows for maximum manufacturing tolerances while maintaining design functionality.',
      tips: [
        'Mention key GD&T symbols',
        'Explain datum reference frames',
        'Compare to traditional plus/minus tolerancing',
      ],
    },
    {
      id: '5',
      question:
        'How would you approach thermal management in a high-performance turbine design?',
      idealAnswer:
        'Thermal management in high-performance turbines involves multiple strategies: implementing cooling systems like film cooling, impingement cooling, and internal cooling passages; selecting high-temperature materials like nickel-based superalloys or ceramic matrix composites; applying thermal barrier coatings to reduce metal temperature; optimizing blade design for heat transfer; conducting comprehensive thermal analysis using CFD and FEA; and implementing active cooling systems where appropriate. The approach must balance thermal efficiency, component life, weight, and manufacturing constraints.',
      tips: [
        'Discuss both active and passive cooling',
        'Mention material selection considerations',
        'Address efficiency trade-offs',
      ],
    },
  ];

  const mockBehavioralQuestions = [
    {
      id: '1',
      question:
        'Tell me about a time when you had to resolve a conflict within your team.',
      idealAnswer:
        'In my previous role at GE, two senior engineers disagreed on the approach for a critical turbine component redesign. I organized a structured meeting where each could present their approach with data. I facilitated by focusing on project objectives rather than personal preferences. We created a decision matrix weighing factors like performance, cost, and timeline. This led to a hybrid solution incorporating strengths from both approaches. The project was completed on schedule, and the component showed a 15% efficiency improvement. This experience taught me that conflicts often lead to better solutions when managed constructively.',
      tips: [
        'Use the STAR method (Situation, Task, Action, Result)',
        'Focus on your specific role in resolution',
        'Highlight positive outcomes',
      ],
    },
    {
      id: '2',
      question:
        'Describe a situation where you had to work under significant pressure to meet a deadline.',
      idealAnswer:
        'At Airbus, we discovered a critical design flaw in a landing gear component just three weeks before production. I was tasked with redesigning while maintaining all safety requirements. I immediately organized my approach: first conducting a root cause analysis, then prioritizing design modifications based on impact. I worked extended hours and leveraged our global team across time zones for continuous progress. I maintained daily stakeholder updates and focused on the most critical simulations first. We delivered the redesign with two days to spare, passed all safety certifications, and implemented new verification protocols to prevent similar issues. This experience reinforced my ability to prioritize effectively under pressure.',
      tips: [
        'Emphasize your planning and prioritization',
        'Mention how you managed stress',
        'Include the successful outcome',
      ],
    },
    {
      id: '3',
      question:
        'Give an example of a time when you had to adapt to a significant change at work.',
      idealAnswer:
        'When my company transitioned from traditional design processes to a model-based definition approach, it required completely changing how we created and documented designs. I volunteered to be part of the pilot implementation team, attended specialized training, and then created simplified guides for my colleagues. I organized lunch-and-learn sessions to demonstrate the new tools and worked one-on-one with team members who were struggling. I also established a feedback system to identify and address pain points. Within three months, our team s productivity returned to previous levels and eventually exceeded them by 20%. This experience showed me that embracing change proactively and supporting others through transition is key to successful adaptation.',
      tips: [
        'Show your proactive approach',
        'Highlight how you helped others adapt',
        'Quantify the successful transition',
      ],
    },
  ];

  // Load prep history when component mounts
  useEffect(() => {
    loadPrepHistory();

    // If coming from a specific job or interview
    if (route.params?.jobPosition) {
      setJobPosition(route.params.jobPosition);
    }
    if (route.params?.company) {
      setCompany(route.params.company);
    }
  }, []);

  // Load prep history
  const loadPrepHistory = () => {
    // Simulate API call
    setTimeout(() => {
      setPrepHistory(mockPrepHistory);
    }, 500);
  };

  // Generate interview questions
  const generateQuestions = () => {
    if (!jobPosition || !company) {
      // Show error or alert
      return;
    }

    setIsGenerating(true);

    // Simulate AI generating questions
    setTimeout(() => {
      if (selectedCategory === 'technical') {
        setGeneratedQuestions(mockTechnicalQuestions);
      } else if (selectedCategory === 'behavioral') {
        setGeneratedQuestions(mockBehavioralQuestions);
      } else {
        // For other categories, use a mix or default to technical
        setGeneratedQuestions(mockTechnicalQuestions);
      }

      setCurrentQuestionIndex(0);
      setUserAnswer('');
      setFeedback(null);
      setShowFeedback(false);
      setIsGenerating(false);

      // Add to history
      const newPrepSession = {
        id: (prepHistory.length + 1).toString(),
        position: jobPosition,
        company: company,
        date: new Date().toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        }),
        category: selectedCategory,
        questionsCount: selectedCategory === 'technical' ? 5 : 3,
        averageScore: 0, // Will be updated as user answers questions
      };

      setPrepHistory([newPrepSession, ...prepHistory]);
    }, 2000);
  };

  // Submit answer for analysis
  const submitAnswer = () => {
    if (!userAnswer.trim()) {
      // Show error or alert
      return;
    }

    setIsAnalyzing(true);

    // Simulate AI analyzing answer
    setTimeout(() => {
      const currentQuestion = generatedQuestions[currentQuestionIndex];

      // Generate mock feedback
      const mockFeedback = {
        score: Math.floor(Math.random() * 30) + 70, // Random score between 70-100
        strengths: [
          'Good technical understanding of core concepts',
          'Clear explanation of key principles',
          'Logical structure to your answer',
        ],
        improvements: [
          'Could provide more specific examples',
          'Consider mentioning industry standards',
          'Elaborate more on practical applications',
        ],
        suggestedAnswer: currentQuestion.idealAnswer,
      };

      setFeedback(mockFeedback);
      setShowFeedback(true);
      setIsAnalyzing(false);

      // Update history with new score
      setPrepHistory(prevHistory => {
        const updatedHistory = [...prevHistory];
        const currentSession = updatedHistory[0];

        // Calculate new average score
        const previousTotal =
          currentSession.averageScore * currentQuestionIndex;
        const newTotal = previousTotal + mockFeedback.score;
        const newAverage = Math.round(newTotal / (currentQuestionIndex + 1));

        updatedHistory[0] = {
          ...currentSession,
          averageScore: newAverage,
        };

        return updatedHistory;
      });
    }, 2000);
  };

  // Move to next question
  const nextQuestion = () => {
    if (currentQuestionIndex < generatedQuestions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
      setUserAnswer('');
      setFeedback(null);
      setShowFeedback(false);
    }
  };

  // Move to previous question
  const prevQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
      setUserAnswer('');
      setFeedback(null);
      setShowFeedback(false);
    }
  };

  // Get category icon
  const getCategoryIcon = categoryId => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.icon : 'help-circle-outline';
  };

  // Get score color
  const getScoreColor = score => {
    if (score >= 90) return '#16a34a'; // Green
    if (score >= 75) return '#0e4e96'; // Blue
    if (score >= 60) return '#f59e0b'; // Orange
    return '#dc2626'; // Red
  };

  // Render category item
  const renderCategoryItem = ({ item }) => (
    <TouchableOpacity
      style={[
        styles.categoryItem,
        selectedCategory === item.id && styles.selectedCategoryItem,
      ]}
      onPress={() => setSelectedCategory(item.id)}
    >
      <Ionicons
        name={item.icon}
        size={24}
        color={selectedCategory === item.id ? '#0e4e96' : '#64748B'}
      />
      <Text
        style={[
          styles.categoryText,
          selectedCategory === item.id && styles.selectedCategoryText,
        ]}
      >
        {item.name}
      </Text>
    </TouchableOpacity>
  );

  // Render history item
  const renderHistoryItem = ({ item }) => (
    <TouchableOpacity
      style={styles.historyItem}
      onPress={() => {
        setJobPosition(item.position);
        setCompany(item.company);
        setSelectedCategory(item.category);
        setActiveTab('prepare');
      }}
    >
      <View style={styles.historyHeader}>
        <View style={styles.historyTitleContainer}>
          <Text style={styles.historyTitle}>{item.position}</Text>
          <Text style={styles.historyCompany}>{item.company}</Text>
        </View>
        <View
          style={[
            styles.historyScoreBadge,
            { backgroundColor: getScoreColor(item.averageScore) + '20' }, // 20% opacity
          ]}
        >
          <Text
            style={[
              styles.historyScoreText,
              { color: getScoreColor(item.averageScore) },
            ]}
          >
            {item.averageScore}%
          </Text>
        </View>
      </View>

      <View style={styles.historyDetails}>
        <View style={styles.historyDetailItem}>
          <Ionicons name="calendar-outline" size={16} color="#64748B" />
          <Text style={styles.historyDetailText}>{item.date}</Text>
        </View>
        <View style={styles.historyDetailItem}>
          <Ionicons
            name={getCategoryIcon(item.category)}
            size={16}
            color="#64748B"
          />
          <Text style={styles.historyDetailText}>
            {categories.find(cat => cat.id === item.category)?.name ||
              'General'}
          </Text>
        </View>
        <View style={styles.historyDetailItem}>
          <Ionicons name="help-circle-outline" size={16} color="#64748B" />
          <Text style={styles.historyDetailText}>
            {item.questionsCount} questions
          </Text>
        </View>
      </View>
    </TouchableOpacity>
  );

  // Render preparation form
  const renderPrepForm = () => (
    <View style={styles.prepFormContainer}>
      <Text style={styles.prepFormTitle}>Interview Preparation</Text>
      <Text style={styles.prepFormSubtitle}>
        Our AI will generate tailored interview questions based on the job
        position and company
      </Text>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Job Position</Text>
        <TextInput
          style={styles.textInput}
          placeholder="e.g. Senior Mechanical Engineer"
          value={jobPosition}
          onChangeText={setJobPosition}
        />
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Company</Text>
        <TextInput
          style={styles.textInput}
          placeholder="e.g. Rolls-Royce"
          value={company}
          onChangeText={setCompany}
        />
      </View>

      <View style={styles.inputContainer}>
        <Text style={styles.inputLabel}>Question Category</Text>
        <FlatList
          data={categories}
          renderItem={renderCategoryItem}
          keyExtractor={item => item.id}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.categoriesList}
        />
      </View>

      <TouchableOpacity
        style={[
          styles.generateButton,
          (!jobPosition || !company) && styles.disabledButton,
        ]}
        onPress={generateQuestions}
        disabled={!jobPosition || !company || isGenerating}
      >
        {isGenerating ? (
          <ActivityIndicator size={20} color="#FFFFFF" />
        ) : (
          <>
            <Ionicons name="bulb-outline" size={20} color="#FFFFFF" />
            <Text style={styles.generateButtonText}>Generate Questions</Text>
          </>
        )}
      </TouchableOpacity>

      <View style={styles.aiInfoContainer}>
        <Ionicons name="information-circle-outline" size={20} color="#64748B" />
        <Text style={styles.aiInfoText}>
          Our AI analyzes thousands of real interviews to generate relevant
          questions and provide personalized feedback
        </Text>
      </View>
    </View>
  );

  // Render question practice
  const renderQuestionPractice = () => {
    if (generatedQuestions.length === 0) {
      return renderPrepForm();
    }

    const currentQuestion = generatedQuestions[currentQuestionIndex];

    return (
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.questionContainer}
      >
        <View style={styles.questionHeader}>
          <Text style={styles.questionPosition}>{jobPosition}</Text>
          <Text style={styles.questionCompany}>{company}</Text>
          <View style={styles.questionProgress}>
            <Text style={styles.questionProgressText}>
              Question {currentQuestionIndex + 1} of {generatedQuestions.length}
            </Text>
            <View style={styles.progressBar}>
              <View
                style={[
                  styles.progressFill,
                  {
                    width: `${((currentQuestionIndex + 1) / generatedQuestions.length) * 100}%`,
                  },
                ]}
              />
            </View>
          </View>
        </View>

        <ScrollView style={styles.questionContent}>
          <View style={styles.questionCard}>
            <Text style={styles.questionText}>{currentQuestion.question}</Text>
          </View>

          {!showFeedback ? (
            <View style={styles.answerContainer}>
              <Text style={styles.answerLabel}>Your Answer</Text>
              <TextInput
                style={styles.answerInput}
                placeholder="Type your answer here..."
                multiline
                value={userAnswer}
                onChangeText={setUserAnswer}
              />

              <TouchableOpacity
                style={[
                  styles.submitButton,
                  !userAnswer.trim() && styles.disabledButton,
                ]}
                onPress={submitAnswer}
                disabled={!userAnswer.trim() || isAnalyzing}
              >
                {isAnalyzing ? (
                  <ActivityIndicator size={20} color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>Submit Answer</Text>
                )}
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.feedbackContainer}>
              <View style={styles.feedbackHeader}>
                <Text style={styles.feedbackTitle}>AI Feedback</Text>
                <View
                  style={[
                    styles.scoreBadge,
                    { backgroundColor: getScoreColor(feedback.score) + '20' }, // 20% opacity
                  ]}
                >
                  <Text
                    style={[
                      styles.scoreText,
                      { color: getScoreColor(feedback.score) },
                    ]}
                  >
                    {feedback.score}%
                  </Text>
                </View>
              </View>

              <View style={styles.feedbackSection}>
                <Text style={styles.feedbackSectionTitle}>Strengths</Text>
                {feedback.strengths.map((strength, index) => (
                  <View key={index} style={styles.feedbackItem}>
                    <Ionicons
                      name="checkmark-circle"
                      size={16}
                      color="#16a34a"
                    />
                    <Text style={styles.feedbackItemText}>{strength}</Text>
                  </View>
                ))}
              </View>

              <View style={styles.feedbackSection}>
                <Text style={styles.feedbackSectionTitle}>
                  Areas for Improvement
                </Text>
                {feedback.improvements.map((improvement, index) => (
                  <View key={index} style={styles.feedbackItem}>
                    <Ionicons
                      name="arrow-up-circle"
                      size={16}
                      color="#f59e0b"
                    />
                    <Text style={styles.feedbackItemText}>{improvement}</Text>
                  </View>
                ))}
              </View>

              <View style={styles.feedbackSection}>
                <Text style={styles.feedbackSectionTitle}>Sample Answer</Text>
                <Text style={styles.sampleAnswerText}>
                  {feedback.suggestedAnswer}
                </Text>
              </View>

              <View style={styles.navigationButtons}>
                <TouchableOpacity
                  style={[
                    styles.navButton,
                    currentQuestionIndex === 0 && styles.disabledNavButton,
                  ]}
                  onPress={prevQuestion}
                  disabled={currentQuestionIndex === 0}
                >
                  <Ionicons
                    name="arrow-back"
                    size={20}
                    color={currentQuestionIndex === 0 ? '#A1A1AA' : '#64748B'}
                  />
                  <Text
                    style={[
                      styles.navButtonText,
                      currentQuestionIndex === 0 &&
                        styles.disabledNavButtonText,
                    ]}
                  >
                    Previous
                  </Text>
                </TouchableOpacity>

                {currentQuestionIndex < generatedQuestions.length - 1 ? (
                  <TouchableOpacity
                    style={styles.navButton}
                    onPress={nextQuestion}
                  >
                    <Text style={styles.navButtonText}>Next</Text>
                    <Ionicons name="arrow-forward" size={20} color="#64748B" />
                  </TouchableOpacity>
                ) : (
                  <TouchableOpacity
                    style={[styles.navButton, styles.finishButton]}
                    onPress={() => {
                      setGeneratedQuestions([]);
                      setActiveTab('history');
                    }}
                  >
                    <Text style={styles.finishButtonText}>Finish Practice</Text>
                    <Ionicons name="checkmark" size={20} color="#FFFFFF" />
                  </TouchableOpacity>
                )}
              </View>
            </View>
          )}
        </ScrollView>
      </KeyboardAvoidingView>
    );
  };

  // Render history tab
  const renderHistoryTab = () => (
    <View style={styles.historyContainer}>
      <Text style={styles.historyTitle}>Practice History</Text>

      {prepHistory.length > 0 ? (
        <FlatList
          data={prepHistory}
          renderItem={renderHistoryItem}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.historyList}
          showsVerticalScrollIndicator={false}
        />
      ) : (
        <View style={styles.emptyHistoryContainer}>
          <Ionicons name="document-text-outline" size={64} color="#E2E8F0" />
          <Text style={styles.emptyHistoryTitle}>No Practice Sessions</Text>
          <Text style={styles.emptyHistoryText}>
            Complete your first interview practice session to see your history
            and track your progress
          </Text>
          <TouchableOpacity
            style={styles.startPracticeButton}
            onPress={() => setActiveTab('prepare')}
          >
            <Text style={styles.startPracticeButtonText}>Start Practice</Text>
          </TouchableOpacity>
        </View>
      )}
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#FFFFFF" />
        </TouchableOpacity>

        <Text style={styles.headerTitle}>AI Interview Prep</Text>

        <TouchableOpacity
          style={styles.headerButton}
          onPress={() => {
            // Show info or help modal
          }}
        >
          <Ionicons name="help-circle-outline" size={24} color="#FFFFFF" />
        </TouchableOpacity>
      </View>

      <View style={styles.tabsContainer}>
        <TouchableOpacity
          style={[styles.tab, activeTab === 'prepare' && styles.activeTab]}
          onPress={() => setActiveTab('prepare')}
        >
          <Ionicons
            name="bulb-outline"
            size={20}
            color={activeTab === 'prepare' ? '#0e4e96' : '#64748B'}
          />
          <Text
            style={[
              styles.tabText,
              activeTab === 'prepare' && styles.activeTabText,
            ]}
          >
            Prepare
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.tab, activeTab === 'history' && styles.activeTab]}
          onPress={() => setActiveTab('history')}
        >
          <Ionicons
            name="time-outline"
            size={20}
            color={activeTab === 'history' ? '#0e4e96' : '#64748B'}
          />
          <Text
            style={[
              styles.tabText,
              activeTab === 'history' && styles.activeTabText,
            ]}
          >
            History
          </Text>
        </TouchableOpacity>
      </View>

      {activeTab === 'prepare' ? renderQuestionPractice() : renderHistoryTab()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  headerButton: {
    padding: 8,
  },
  tabsContainer: {
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  tab: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
  },
  activeTab: {
    borderBottomWidth: 2,
    borderBottomColor: '#0e4e96',
  },
  tabText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginLeft: 6,
  },
  activeTabText: {
    color: '#0e4e96',
    fontWeight: '600',
  },
  prepFormContainer: {
    padding: 16,
  },
  prepFormTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 8,
  },
  prepFormSubtitle: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 24,
  },
  inputContainer: {
    marginBottom: 16,
  },
  inputLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#334155',
    marginBottom: 8,
  },
  textInput: {
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 16,
  },
  categoriesList: {
    paddingVertical: 8,
  },
  categoryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    marginRight: 8,
  },
  selectedCategoryItem: {
    backgroundColor: '#E0F2FE',
  },
  categoryText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginLeft: 6,
  },
  selectedCategoryText: {
    color: '#0e4e96',
  },
  generateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    borderRadius: 8,
    marginTop: 8,
  },
  disabledButton: {
    backgroundColor: '#94A3B8',
  },
  generateButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
    marginLeft: 8,
  },
  aiInfoContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    padding: 12,
    borderRadius: 8,
    marginTop: 24,
  },
  aiInfoText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 8,
    flex: 1,
  },
  questionContainer: {
    flex: 1,
  },
  questionHeader: {
    padding: 16,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  questionPosition: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  questionCompany: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 12,
  },
  questionProgress: {
    marginTop: 8,
  },
  questionProgressText: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 4,
  },
  progressBar: {
    height: 4,
    backgroundColor: '#E2E8F0',
    borderRadius: 2,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    backgroundColor: '#0e4e96',
  },
  questionContent: {
    flex: 1,
    padding: 16,
  },
  questionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  questionText: {
    fontSize: 16,
    color: '#0f172a',
    lineHeight: 24,
  },
  answerContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  answerLabel: {
    fontSize: 14,
    fontWeight: '500',
    color: '#334155',
    marginBottom: 8,
  },
  answerInput: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    minHeight: 150,
    textAlignVertical: 'top',
  },
  submitButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 16,
  },
  submitButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  feedbackContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  feedbackHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  feedbackTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  scoreBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  scoreText: {
    fontSize: 14,
    fontWeight: '600',
  },
  feedbackSection: {
    marginBottom: 16,
  },
  feedbackSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#334155',
    marginBottom: 8,
  },
  feedbackItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  feedbackItemText: {
    fontSize: 14,
    color: '#334155',
    marginLeft: 8,
    flex: 1,
  },
  sampleAnswerText: {
    fontSize: 14,
    color: '#334155',
    lineHeight: 20,
    backgroundColor: '#F8FAFC',
    padding: 12,
    borderRadius: 8,
  },
  navigationButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16,
  },
  navButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 8,
    paddingHorizontal: 12,
  },
  navButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#64748B',
    marginHorizontal: 4,
  },
  disabledNavButton: {
    opacity: 0.5,
  },
  disabledNavButtonText: {
    color: '#A1A1AA',
  },
  finishButton: {
    backgroundColor: '#0e4e96',
    borderRadius: 8,
  },
  finishButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FFFFFF',
    marginHorizontal: 4,
  },
  historyContainer: {
    flex: 1,
    padding: 16,
  },
  historyTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 16,
  },
  historyList: {
    paddingBottom: 16,
  },
  historyItem: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  historyHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  historyTitleContainer: {
    flex: 1,
  },
  historyTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  historyCompany: {
    fontSize: 14,
    color: '#64748B',
  },
  historyScoreBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  historyScoreText: {
    fontSize: 14,
    fontWeight: '600',
  },
  historyDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  historyDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 16,
    marginBottom: 4,
  },
  historyDetailText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 4,
  },
  emptyHistoryContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  emptyHistoryTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#64748B',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyHistoryText: {
    fontSize: 14,
    color: '#94A3B8',
    textAlign: 'center',
    marginBottom: 24,
  },
  startPracticeButton: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 24,
    borderRadius: 8,
  },
  startPracticeButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#FFFFFF',
  },
});
